import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Form,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { configOptionApi, GeneralSettings } from "../../services/configOption.api";
import { propiedadesApi } from "../../services/propiedades.api";
import { formatPropertyName } from "../../utils/propertyDisplay";

const { Text, Title, Paragraph } = Typography;

/** Solo las tres claves de correo del singleton `general-settings`. */
type CorreoSettings = Required<
  Pick<
    GeneralSettings,
    "correosNotificacionActivos" | "correoCierreMensual" | "correoTareaCompartida"
  >
>;

const DEFAULTS: CorreoSettings = {
  correosNotificacionActivos: true,
  correoCierreMensual: true,
  correoTareaCompartida: true,
};

/**
 * NotificacionesTab — pestaña "Correos al cliente" de /configuracion.
 *
 * Apaga los correos de AVISO al cliente (round 2026-07-28, pedido del dueño):
 * el maestro los corta todos, y cada tipo se puede silenciar por separado.
 *
 * LA DISTINCIÓN QUE HAY QUE TENER CLARA — y por eso está escrita también en la
 * pantalla: esto NO toca los correos transaccionales. La recuperación de
 * contraseña y el correo de bienvenida (que lleva la contraseña inicial) siguen
 * saliendo con todo apagado. Si alguna vez se agrega un correo nuevo, decidir
 * primero de cuál de los dos grupos es: apagar un transaccional deja a una
 * persona sin poder entrar al sistema.
 *
 * La capa granular vive en la ficha de cada propiedad
 * (`propiedades.correos_cliente_activos`); acá solo se muestran cuáles están
 * silenciadas, para que ese interruptor no quede invisible.
 *
 * Backend: utils/notificacionesCorreo.js. Ver docs/integraciones-externas.md §5.
 */
export default function NotificacionesTab() {
  const [form] = Form.useForm<CorreoSettings>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maestro, setMaestro] = useState(true);
  const [silenciadas, setSilenciadas] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await configOptionApi.getGeneralSettings();
    const valores: CorreoSettings = {
      correosNotificacionActivos:
        res?.data?.correosNotificacionActivos ?? DEFAULTS.correosNotificacionActivos,
      correoCierreMensual: res?.data?.correoCierreMensual ?? DEFAULTS.correoCierreMensual,
      correoTareaCompartida:
        res?.data?.correoTareaCompartida ?? DEFAULTS.correoTareaCompartida,
    };
    form.setFieldsValue(valores);
    setMaestro(valores.correosNotificacionActivos);
    setLoading(false);
  }, [form]);

  /**
   * Propiedades con los avisos apagados en su propia ficha. Es informativo: no
   * se editan desde acá (se cambian en la propiedad), pero sin esta lista el
   * admin no tendría dónde ver de una sola mirada a quién dejó de escribirle.
   */
  const loadSilenciadas = useCallback(async () => {
    const res = await propiedadesApi.getAll({ limit: 400 });
    const filas = Array.isArray(res?.data) ? res.data : [];
    setSilenciadas(
      filas
        .filter((p: any) => p?.correos_cliente_activos === false)
        .map((p: any) => String(p?.nombrepropiedad || `Propiedad #${p?.id}`)),
    );
  }, []);

  useEffect(() => {
    void load();
    void loadSilenciadas();
  }, [load, loadSilenciadas]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const res = await configOptionApi.updateGeneralSettings(values);
      if (res?.data) setMaestro(res.data.correosNotificacionActivos !== false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={5}>Correos automáticos al cliente</Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Qué apagan estos interruptores"
        description={
          <>
            <div>
              Apagan solo los <b>avisos</b>: correos que anuncian algo que el
              cliente ya puede ver entrando a la plataforma. Si no llegan, no
              pierde información — se entera al entrar.
            </div>
            <div style={{ marginTop: 8 }}>
              <b>No afectan</b> a la recuperación de contraseña ni al correo de
              bienvenida (el que lleva la contraseña inicial). Esos siguen
              saliendo siempre: sin ellos, una persona quedaría sin poder entrar
              al sistema.
            </div>
          </>
        }
      />

      <Form form={form} layout="horizontal" initialValues={DEFAULTS}>
        <Form.Item
          name="correosNotificacionActivos"
          label="Enviar avisos por correo a los clientes"
          valuePropName="checked"
          tooltip="Interruptor maestro. Apagado, no sale ningún aviso, sin importar cómo estén los de abajo."
        >
          <Switch onChange={setMaestro} />
        </Form.Item>

        {!maestro && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Los avisos por correo están apagados"
            description="Publicar un cierre de mes o compartir una observación seguirá funcionando igual, pero el cliente no recibirá ningún correo."
          />
        )}

        <Divider style={{ margin: "8px 0 16px" }} />

        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Con el maestro encendido, puedes silenciar un tipo de aviso y dejar el
          otro:
        </Paragraph>

        <Form.Item
          name="correoCierreMensual"
          label="Cierre de mes publicado"
          valuePropName="checked"
          tooltip="Se envía al publicar el cierre de una propiedad desde Finanzas de empresa. Incluye el recordatorio de las observaciones compartidas que el cliente tenga abiertas."
        >
          <Switch disabled={!maestro} />
        </Form.Item>

        <Form.Item
          name="correoTareaCompartida"
          label="Observación compartida con el cliente"
          valuePropName="checked"
          tooltip="Se envía cuando una tarea pasa a ser visible para el cliente. Solo la primera vez: editarla después no vuelve a avisar."
        >
          <Switch disabled={!maestro} />
        </Form.Item>

        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
        >
          Guardar
        </Button>
      </Form>

      <Divider />

      <Title level={5}>Propiedades silenciadas</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Además del interruptor general, cada propiedad tiene el suyo en su ficha
        (Propiedades → editar → Identificación). Un aviso sale solo si el general{" "}
        <b>y</b> el de la propiedad están encendidos.
      </Paragraph>
      {silenciadas.length ? (
        <Space size={[4, 8]} wrap>
          {silenciadas.map((nombre) => (
            <Tag key={nombre} color="orange">
              {formatPropertyName(nombre)}
            </Tag>
          ))}
        </Space>
      ) : (
        <Text type="secondary">
          Ninguna propiedad tiene los avisos apagados en su ficha.
        </Text>
      )}
    </div>
  );
}
